import * as d3 from "d3";
import Utils from "../Utility/Utils";

export default class RegionAVGGraph {
    constructor(chartArea, data) {
        this.MARGIN = { LEFT: 40, RIGHT: 0, TOP: 20, BOTTOM: 130 };
        this.WIDTH = chartArea.clientWidth;
        this.HEIGHT = window.innerHeight <= 750 ? 100 : 150;
        this.chartArea = chartArea
        this.data = data

        this.groupBorderColor = [
            "#7054CC",
            "#007D59",
            "#21405C",
            "#00B0FF",
            "#B07D24",
            "#026CCA",
        ]

        this.groupColors = [
            "#9487E5",
            "#00A699",
            "#427DA1",
            "#4AD1FF",
            "#EDBA00",
            "#359FFD",
        ]

        this.groupBackgroundColors = [
            "#CCC9ED",
            "#4ADEC7",
            "#CCDEEB",
            "#4DFFFA",
            "#FFDB7A",
            "#B3DBFE",
        ]



    }


    clearGraph() {
        this.chartArea.innerHTML = ""
    }

    updateGraph(region) {
        const filteredData = this.calculateRegionAvg(region)
        this.clearGraph()
        const avgTotal = document.querySelector('.experience .left_detail_panel .score .point')
        avgTotal.textContent = filteredData.totalAverage.toFixed(1)

        this.svg = d3
            .select(this.chartArea)
            .append("svg")
            .attr("width", this.WIDTH)
            .attr("height", this.HEIGHT);



        const averages = Utils.getSegmentWiseAverage(filteredData.segmentAverages)
        const updatedData = [
            { label: "Labor and human capital", value: averages[0].value },
            { label: "Physical capital", value: averages[1].value },
            { label: "Innovation and intangible capital", value: averages[4].value },
            { label: "Institutions", value: averages[5].value },
            { label: "Natural capital", value: averages[3].value },
            { label: "Social capital", value: averages[2].value },
        ]


        const xScale = d3
            .scaleBand()
            .domain(updatedData.map((d, i) => i))
            .range([10, this.WIDTH - 10])
            .paddingInner(0.7)
            .paddingOuter(0.2);


        const yScale = d3
            .scaleLinear()
            .domain([
                d3.min(updatedData, d => d.value),
                d3.max(updatedData, d => d.value)
            ])
            .range([this.HEIGHT, this.MARGIN.TOP]);

        const baseline = yScale(0);

        this.svg
            .append("g")
            .attr("class", "line-group")
            .selectAll("line")
            .data(updatedData)
            .join("line")
            .attr("x1", (d, i) => xScale(i) + xScale.bandwidth() / 2)
            .attr("x2", (d, i) => xScale(i) + xScale.bandwidth() / 2)
            .attr("y1", 0)
            .attr("y2", this.HEIGHT)
            .attr("stroke", "rgba(255, 255, 255, 0.1)")
            .attr("stroke-width", d => (d ? 1 : 0))


        const blackGradient = this.svg.append("defs")
            .append("linearGradient")
            .attr("id", "blackGradient")
            .attr("x1", "0%")
            .attr("y1", "100%")
            .attr("x2", "0%")
            .attr("y2", "0%")
            .selectAll("stop")
            .data([
                { offset: "0%", color: "black", opacity: 0 },
                { offset: "100%", color: "black", opacity: 0 }
            ])
            .enter().append("stop")
            .attr("offset", d => d.offset)
            .attr("stop-color", d => d.color)
            .attr("stop-opacity", d => d.opacity);

        this.svg
            .append("g")
            .selectAll("rect")
            .data(updatedData)
            .join("rect")
            .attr("x", (d, i) => xScale(i))
            .attr("data-label", d => d != null ? d.label : "")
            .attr("y", baseline)
            .attr("height", 0)
            .attr("width", xScale.bandwidth())
            .attr("fill", (d, i) => (d ? this.groupBackgroundColors[i] : "none"))
            .attr("opacity", 1)
            .transition()
            .duration(1000)
            .ease(d3.easePoly.exponent(1))
            .attr("y", d => (d?.value >= 0 ? yScale(d.value) : baseline))
            .attr("height", d => (d ? Math.abs(yScale(d.value) - baseline) : 0));

        this.svg
            .append("g")
            .selectAll("circle")
            .data(updatedData)
            .join("circle")
            .attr("cx", (d, i) => xScale(i) + xScale.bandwidth() / 2)
            .attr("cy", baseline)
            .attr("r", parseInt(xScale.bandwidth() / 2))
            .attr("fill", (d, i) => (d ? this.groupColors[i] : "none"))
            .attr("stroke", (d, i) => (d ? this.groupBorderColor[i] : "none"))
            .attr("stroke-width", 1)
            .transition()
            .duration(1000)
            .ease(d3.easePoly.exponent(1))
            .attr("cy", d => (d ? yScale(d.value) : baseline))

        
        const xLabels = `
            <ul class="x-labels">
                <li><p>Labor and<br>human capital</p></li>
                <li><p>Physical capital</p></li>
                <li><p>Innovation and<br>intangible capital</p></li>
                <li><p>Institutions</p></li>
                <li><p>Natural capital</p></li>
                <li><p>Social capital</p></li>
            </ul>
        `

        this.chartArea.insertAdjacentHTML("beforeend", xLabels);
        
 
    }


    calculateRegionAvg(region) {
        // Filter the data for the specified region
        const regionalData = this.data.filter(country => country.Region === region);

        // Check if the region has any countries
        if (regionalData.length === 0) {
            console.warn(`No data found for the region: ${region}`);
            return;
        }

        // Define the segments for calculation
        const segmentKeys = [
            "Human_capital",
            "Natural_capital",
            "Percent_of_population",
            "Trust",
            "World_Bank_institutional",
            "Inequality_principal",
            "Hirschman_Index",
            "Physical_capital",
            "World_Bank_logistics",
            "Patent_applications",
            "Science_journal",
            "PM2_5",
            "Access_to_electricity",
            "Individuals_using_internet",
            "Suicide_mortality",
            "Secure_internet",
            "Water_stress",
            "Age_dependency_ratio",
            "Life_expectancy"
        ];

        // Calculate the segment averages
        const segmentAverages = segmentKeys.reduce((averages, key) => {
            const total = regionalData.reduce((sum, country) => sum + country[key], 0);
            averages[key] = total / regionalData.length; // Calculate average for each segment
            return averages;
        }, {});

        // Calculate the overall total average
        const totalAverage = regionalData.reduce((sum, country) => sum + country.Total, 0) / regionalData.length;

        // Combine results into a single object
        const result = {
            region: region,
            totalAverage: totalAverage,
            segmentAverages: segmentAverages
        };

        return result;
    }

}



